import styled from "@emotion/styled";
import {
  AllCaps,
  CtaBox,
  IconChatHeart,
  IconEnvelopeHeart,
  IconFilter,
  IconGraduationCap,
  IconUser,
  Link,
  Text,
} from "atoms";
import hugArtistProfileImg from "assets/images/hug-artist-application-img.png";
import { PlatformRoles, User } from "features/auth";
import { useHomePageVersion } from "../api/useHomePageVersion";

type HomeCtaBlocksProps = {
  user?: User;
  roles: PlatformRoles;
};

function HomeCtaBlocks({ roles, user }: HomeCtaBlocksProps) {
  const { version, claimedArtistId } = useHomePageVersion({
    user,
    roles,
  });

  if (version === "LOGGED_IN_CLAIMED")
    return (
      <CtasWrapper>
        <HomeCtaBox
          title="Customize Your Artist&nbsp;Profile"
          text={
            <Text>
              Now that you&apos;ve claimed your Artist Profile, keep it up to
              date by showcasing your latest collections or accepting commission
              requests!
            </Text>
          }
          linkText="Edit Profile"
          linkUrl={`/artists/${claimedArtistId}/edit`}
          icon={<IconFilter accent="accent1" />}
        />
        <HomeCtaBox
          title="Invite an Artist"
          text={
            <Text>
              Invite a fellow artist to join you on HUG! For every artist that
              you invite, you earn HUG Points that can be exchanged for rewards,
              such as free art and merchandise.
            </Text>
          }
          linkText="Start Inviting"
          linkUrl="/artist-invites"
          icon={<IconUser accent="accent2" />}
        />
        <HomeCtaBox
          title="Get Free Artist Education"
          text={
            <Text>
              Our weekly creator-focused newsletter, Creator Royalties, offers
              easy-to-understand Web3 education, news, opportunities, art drops,
              and&nbsp;more.
            </Text>
          }
          linkText="Subscribe Now"
          linkUrl="https://creatorroyalties.beehiiv.com/subscribe"
          linkTarget="_blank"
          icon={<IconGraduationCap accent="accent3" />}
        />
      </CtasWrapper>
    );
  if (version === "LOGGED_IN_HOLDER")
    return (
      <CtasWrapper>
        <HomeCtaBox
          title="Leave a Review"
          text={
            <Text>
              Earn <AllCaps>Hug</AllCaps> Points for leaving any artist
              a&nbsp;review! <em>Pro&nbsp;tip:</em> You&nbsp;get double the
              points for artists with less than 5&nbsp;reviews.
            </Text>
          }
          linkText="Start Earning"
          linkUrl="/search/?sort=reviewCount"
          icon={<IconChatHeart accent="accent1" />}
        />
        <HomeCtaBox
          title="Invite an Artist"
          text={
            <Text>
              Invite a fellow artist to join you on HUG! For every artist that
              you invite, you earn HUG Points that can be exchanged for rewards,
              such as free art and merchandise.
            </Text>
          }
          linkText="Start Inviting"
          linkUrl="/artist-invites"
          icon={<IconUser accent="accent2" />}
        />
        <HomeCtaBox
          title="Commission Custom Artwork"
          text={
            <Text>
              Work together with an artist to bring your ideas to&nbsp;life.
              Send&nbsp;a request through their <AllCaps>Hug</AllCaps> Profile
              to start the conversation today.
            </Text>
          }
          linkText="See Participating Artists"
          linkUrl="/commissions"
          icon={<IconEnvelopeHeart accent="accent4" />}
        />
      </CtasWrapper>
    );
  if (version === "LOGGED_IN")
    return (
      <CtasWrapper>
        <HomeCtaBox
          title="Leave a Review"
          text={
            <Text>
              Make an artist&apos;s day! Help an artist get discovered by
              telling them how much you love their work and why.
            </Text>
          }
          linkText="Find Artists to Review"
          linkUrl="/search/?type=artist&sort=reviewCount"
          icon={<IconChatHeart accent="accent1" />}
        />
        <HomeCtaBox
          title="Invite an Artist"
          text={
            <Text>
              Invite a fellow artist to join you on HUG! For every artist that
              you invite, you earn HUG Points that can be exchanged for rewards,
              such as free art and merchandise.
            </Text>
          }
          linkText="Start Inviting"
          linkUrl="/artist-invites"
          icon={<IconUser accent="accent2" />}
        />
        <HomeCtaBox
          title="Become a Curator"
          text={
            <Text>
              Get a <AllCaps>Hug</AllCaps> Pass to become a Curator. Curators
              earn <AllCaps>Hug</AllCaps>&nbsp;Points for influencing artist
              discovery on <AllCaps>Hug</AllCaps> through voting and moderation.
            </Text>
          }
          linkText={
            <>
              Get a <AllCaps>Hug</AllCaps> Pass
            </>
          }
          linkUrl="https://opensea.io/collection/hugpass"
          icon={<IconUser accent="accent1" />}
        />
      </CtasWrapper>
    );
  return (
    <CtasWrapper>
      <HomeCtaBox
        title="Apply For Your Artist Profile"
        text={
          <Text>
            Showcase all your collections and social links in one&nbsp;place.{" "}
            <em>Bonus:</em> You also get to collect meaningful reviews from
            collectors who love&nbsp;you!
          </Text>
        }
        button={
          <>
            <Link variant="primary" to="/artist-application" size="xs">
              Apply Now
            </Link>
            <Link variant="secondary" to="/artist-profile-benefits" size="xs">
              Learn More
            </Link>
          </>
        }
        icon={<IconUser accent="accent1" />}
        imageSrc={hugArtistProfileImg}
      />
      <HomeCtaBox
        title="Get Free Artist&nbsp;Education"
        text={
          <Text>
            Our weekly creator-focused newsletter, Creator Royalties, offers
            easy-to-understand Web3 education, news, opportunities, art drops,
            and&nbsp;more.
          </Text>
        }
        linkText="Subscribe Now"
        linkUrl="https://creatorroyalties.beehiiv.com/subscribe"
        linkTarget="_blank"
        icon={<IconGraduationCap accent="accent3" />}
      />
    </CtasWrapper>
  );
}

const CtasWrapper = styled.div(({ theme }) => ({
  alignItems: "stretch",
  display: "flex",
  gap: theme.spacing.gap,
  flexWrap: "wrap",
}));

const HomeCtaBox = styled(CtaBox)<{ imageSrc?: string; border?: boolean }>(
  ({ imageSrc, theme }) => ({
    flex: `10 0 calc(250px - ${theme.spacing.gap})`,
    ...(imageSrc && {
      flex: `1 0 calc((100% / 3) * 2 - ${theme.spacing.gap})`,
    }),
  })
);
HomeCtaBox.defaultProps = { border: true };

export { HomeCtaBlocks };
